import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { vulnerabilitiesAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import {
  Shield,
  AlertTriangle,
  Info,
  XCircle,
  CheckCircle,
  Clock,
  Filter,
  Download,
  Eye,
  Trash2,
  Edit,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  AlarmClock,
  Settings,
  Play,
  Loader,
} from 'lucide-react';

// Only allow http(s) in rendered hrefs. A vulnerability's url/references can
// carry attacker-influenced values (e.g. a `javascript:` URI); auto-enrichment
// now populates them without manual review, so an unsanitized href would be a
// stored XSS. A rejected value yields no href (the text is still shown).
const safeHref = (u) => {
  if (typeof u !== 'string') return undefined;
  const t = u.trim();
  return /^https?:\/\//i.test(t) ? t : undefined;
};

const Vulnerabilities = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { vulnId: routeVulnId } = useParams();
  const navigate = useNavigate();
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    severity: searchParams.get('severity') || '',
    status: '',
    host: searchParams.get('host') || '',
    researcher: searchParams.get('researcher') || '',
    attention: '',
    overdue: false,
  });
  const [researchers, setResearchers] = useState([]);
  const [hasUnassigned, setHasUnassigned] = useState(false);
  const [myAttention, setMyAttention] = useState(null);
  const currentUsername = localStorage.getItem('username') || '';
  const [sort, setSort] = useState({ by: 'created_at', dir: 'desc' });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });
  const [selectedVuln, setSelectedVuln] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importPayload, setImportPayload] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  // Pyrus Sync state
  const isAdmin = localStorage.getItem('userRole') === 'admin';
  const [showPyrusSyncModal, setShowPyrusSyncModal] = useState(false);
  const [pyrusConfig, setPyrusConfig] = useState(null);
  const [pyrusConfigForm, setPyrusConfigForm] = useState({});
  const [pyrusConfigLoading, setPyrusConfigLoading] = useState(false);
  const [pyrusSyncRuns, setPyrusSyncRuns] = useState([]);
  const [pyrusSyncRunning, setPyrusSyncRunning] = useState(false);

  const { showSuccess, showError } = useNotification();

  // Handle URL parameters on mount
  useEffect(() => {
    const hostParam = searchParams.get('host');
    const severityParam = searchParams.get('severity');
    
    if (hostParam || severityParam) {
      // Preserve the other active filters (researcher/attention/overdue/status):
      // the URL only drives severity + host. Replacing the whole object dropped
      // them and briefly turned those controlled <select> inputs uncontrolled.
      setFilters((prev) => ({
        ...prev,
        severity: severityParam || '',
        host: hostParam || '',
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchVulnerabilities();
    fetchStats();
  }, [pagination.page, filters, sort]);

  useEffect(() => {
    fetchResearchers();
    fetchMyAttention();
  }, []);

  // Deep-link: open vulnerability detail modal when URL contains :vulnId.
  // Refetches whenever the path id changes (e.g. user pastes a fresh URL).
  useEffect(() => {
    if (!routeVulnId) {
      // URL no longer pointing at a specific vuln — close modal if it's open.
      if (showDetailModal) {
        setShowDetailModal(false);
        setSelectedVuln(null);
      }
      return;
    }
    if (selectedVuln && selectedVuln._id === routeVulnId && showDetailModal) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await vulnerabilitiesAPI.getVulnerability(routeVulnId);
        if (cancelled) return;
        setSelectedVuln(res.data);
        setShowDetailModal(true);
      } catch (err) {
        if (cancelled) return;
        const code = err.response?.status;
        if (code === 404) {
          showError('Уязвимость не найдена');
        } else {
          showError(err.response?.data?.message || 'Не удалось открыть уязвимость');
        }
        // Drop the invalid id from the URL but keep filters.
        const qs = searchParams.toString();
        navigate(qs ? `/vulnerabilities?${qs}` : '/vulnerabilities', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeVulnId]);

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedVuln(null);
    if (routeVulnId) {
      const qs = searchParams.toString();
      navigate(qs ? `/vulnerabilities?${qs}` : '/vulnerabilities');
    }
  };

  const fetchResearchers = async () => {
    try {
      const res = await vulnerabilitiesAPI.listResearchers();
      setResearchers(res.data.researchers || []);
      setHasUnassigned(!!res.data.has_unassigned);
    } catch (err) {
      console.error('Не удалось получить список исследователей:', err);
    }
  };

  const fetchMyAttention = async () => {
    try {
      const res = await vulnerabilitiesAPI.getMyAttention();
      setMyAttention(res.data);
    } catch (err) {
      // ignore — feature is non-critical
    }
  };

  const applyMyAttention = () => {
    setFilters((prev) => ({
      ...prev,
      attention: prev.attention === 'mine' ? '' : 'mine',
      researcher: '',
      status: '',
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const fetchVulnerabilities = async () => {
    try {
      setLoading(true);
      const response = await vulnerabilitiesAPI.getVulnerabilities(
        pagination.page,
        pagination.limit,
        filters,
        sort
      );
      setVulnerabilities(response.data.vulnerabilities);
      setPagination((prev) => ({
        ...prev,
        total: response.data.total,
      }));
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось получить уязвимости');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await vulnerabilitiesAPI.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Не удалось получить статистику:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (vulnId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту уязвимость?')) return;

    try {
      await vulnerabilitiesAPI.deleteVulnerability(vulnId);
      showSuccess('Уязвимость успешно удалена');
      fetchVulnerabilities();
      fetchStats();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось удалить уязвимость');
    }
  };

  const handleViewDetails = async (vuln) => {
    setSelectedVuln(vuln);
    setShowDetailModal(true);
    // Reflect selection in URL so the link is shareable. Preserve filters.
    const qs = searchParams.toString();
    const target = qs ? `/vulnerabilities/${vuln._id}?${qs}` : `/vulnerabilities/${vuln._id}`;
    if (routeVulnId !== vuln._id) {
      navigate(target);
    }
  };

  const handleUpdateStatus = async (vulnId, newStatus) => {
    try {
      await vulnerabilitiesAPI.updateVulnerability(vulnId, { status: newStatus });
      showSuccess('Статус успешно обновлён');
      fetchVulnerabilities();
      fetchStats();
      fetchMyAttention();
      if (selectedVuln && selectedVuln._id === vulnId) {
        setSelectedVuln({ ...selectedVuln, status: newStatus });
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось обновить статус');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFileName(file.name);
    setImportLoading(true);
    try {
      const text = await file.text();
      let parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) parsed = parsed.vulnerabilities || [parsed];
      setImportPayload(parsed);
      const res = await vulnerabilitiesAPI.importVulnerabilities(parsed, true);
      setImportSummary(res.data);
    } catch (err) {
      showError('Ошибка при чтении файла: ' + (err.message || err));
      setImportSummary(null);
    } finally {
      setImportLoading(false);
    }
  };

  const applyImport = async () => {
    setImportLoading(true);
    try {
      const res = await vulnerabilitiesAPI.importVulnerabilities(importPayload, false);
      showSuccess(`Импортировано ${res.data.created} уязвимостей`);
      setShowImportModal(false);
      setImportSummary(null);
      setImportPayload([]);
      fetchVulnerabilities();
      fetchStats();
    } catch (err) {
      showError(err.response?.data?.message || 'Ошибка импорта');
    } finally {
      setImportLoading(false);
    }
  };

  // Pyrus Sync handlers
  const fetchPyrusConfig = async () => {
    try {
      setPyrusConfigLoading(true);
      const res = await vulnerabilitiesAPI.getPyrusSyncConfig();
      setPyrusConfig(res.data);
      setPyrusConfigForm({
        enabled: res.data.enabled || false,
        base_url: res.data.base_url || '',
        login: res.data.login || '',
        security_key: '',
        registry_id: res.data.registry_id || '',
        schedule_type: res.data.schedule?.type || 'interval',
        schedule_expr: res.data.schedule?.expr || '24h',
        request_delay_ms: res.data.request_delay_ms ?? 100,
      });
    } catch (err) {
      showError('Не удалось загрузить конфигурацию Pyrus');
    } finally {
      setPyrusConfigLoading(false);
    }
  };

  const fetchPyrusSyncRuns = async () => {
    try {
      const res = await vulnerabilitiesAPI.listPyrusSyncRuns(1, 10);
      setPyrusSyncRuns(res.data.runs || []);
    } catch (err) {
      console.error('Failed to fetch pyrus sync runs:', err);
    }
  };

  const savePyrusConfig = async () => {
    try {
      setPyrusConfigLoading(true);
      const payload = {
        enabled: pyrusConfigForm.enabled,
        base_url: pyrusConfigForm.base_url,
        login: pyrusConfigForm.login,
        registry_id: pyrusConfigForm.registry_id ? Number(pyrusConfigForm.registry_id) : null,
        schedule: { type: pyrusConfigForm.schedule_type, expr: pyrusConfigForm.schedule_expr },
        request_delay_ms: Number(pyrusConfigForm.request_delay_ms) || 100,
      };
      if (pyrusConfigForm.security_key) {
        payload.security_key = pyrusConfigForm.security_key;
      }
      const res = await vulnerabilitiesAPI.updatePyrusSyncConfig(payload);
      setPyrusConfig(res.data);
      setPyrusConfigForm((prev) => ({ ...prev, security_key: '' }));
      showSuccess('Конфигурация Pyrus сохранена');
    } catch (err) {
      showError(err.response?.data?.message || 'Ошибка сохранения конфигурации');
    } finally {
      setPyrusConfigLoading(false);
    }
  };

  const runPyrusSyncNow = async () => {
    try {
      setPyrusSyncRunning(true);
      await vulnerabilitiesAPI.runPyrusSyncNow();
      showSuccess('Синхронизация Pyrus запущена');
      setTimeout(fetchPyrusSyncRuns, 2000);
    } catch (err) {
      showError(err.response?.data?.message || 'Ошибка запуска синхронизации');
    } finally {
      setPyrusSyncRunning(false);
    }
  };

  const openPyrusSyncModal = () => {
    setShowPyrusSyncModal(true);
    fetchPyrusConfig();
    fetchPyrusSyncRuns();
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return '#dc2626';
      case 'high':
        return '#ea580c';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#10b981';
      case 'info':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return <XCircle size={18} />;
      case 'medium':
        return <AlertTriangle size={18} />;
      case 'low':
      case 'info':
        return <Info size={18} />;
      default:
        return <Shield size={18} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return '#dc2626';
      case 'confirmed':
        return '#ea580c';
      case 'fixed':
        return '#10b981';
      case 'false_positive':
        return '#6b7280';
      case 'accepted':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getExpectedCloseDate = (vuln) => (
    vuln.metadata?.expected_close_date || vuln.metadata?.due_date
  );

  const hasIndividualDeadline = (vuln) => (
    (vuln.metadata?.individual_deadline_enabled === true ||
      vuln.metadata?.individual_deadline_enabled === 'checked') &&
    !!vuln.metadata?.planned_fix_date
  );

  const isOverdue = (vuln) => {
    const due = getExpectedCloseDate(vuln);
    if (!due) return false;
    if (vuln.status === 'fixed' || vuln.status === 'false_positive') return false;
    return due < new Date().toISOString().slice(0, 10);
  };

  const isMineUnclosed = (vuln) => {
    if (!currentUsername) return false;
    const r = vuln.researcher || vuln.metadata?.researcher;
    if (!r || r !== currentUsername) return false;
    return !['fixed', 'false_positive', 'accepted'].includes(vuln.status);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  };

  const handleSort = (field) => {
    setSort((prev) => ({
      by: field,
      dir: prev.by === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const SortIcon = ({ field }) => {
    if (sort.by !== field) return <ArrowUpDown size={13} style={{ opacity: 0.4, marginLeft: 4 }} />;
    return sort.dir === 'asc'
      ? <ArrowUp size={13} style={{ marginLeft: 4 }} />
      : <ArrowDown size={13} style={{ marginLeft: 4 }} />;
  };

  const stripPyrusFromDescription = (desc) => {
    if (!desc) return '';
    return desc.replace(/\n\n\*\*Задача Pyrus:\*\*.*$/s, '').trim();
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div style={{ padding: '28px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-icon" style={{ background: 'linear-gradient(135deg, #ef4444, #f59e0b)' }}>
          <Shield size={24} color="white" />
        </div>
        <div className="page-header-text">
          <h1>Уязвимости</h1>
          <p>Результаты проверки безопасности</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
              {stats.total}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Всего</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>
              {stats.by_severity.critical}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Критические</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ea580c' }}>
              {stats.by_severity.high}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Высокие</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
              {stats.by_severity.medium}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Средние</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
              {stats.by_severity.low}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Низкие</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>
              {stats.by_status.open}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Открытые</div>
          </div>
        </div>
      )}

      {/* Personal attention banner */}
      {myAttention && myAttention.count > 0 && (
        <div
          onClick={applyMyAttention}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px', marginBottom: 16, cursor: 'pointer',
            border: `1px solid ${filters.attention === 'mine' ? '#dc2626' : '#f59e0b'}`,
            borderRadius: 8,
            background: filters.attention === 'mine' ? 'rgba(220,38,38,0.08)' : 'rgba(245,158,11,0.08)',
            transition: 'background 0.2s',
          }}
          title="Открыть только мои незакрытые уязвимости"
        >
          <AlertTriangle size={20} color={filters.attention === 'mine' ? '#dc2626' : '#f59e0b'} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              На вас назначено {myAttention.count} незакрытых уязвимостей
              {currentUsername ? ` (${currentUsername})` : ''}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {Object.entries(myAttention.by_severity || {})
                .sort(([a], [b]) => {
                  const order = { critical: 0, high: 1, medium: 2, low: 3, info: 4, unknown: 5 };
                  return (order[a] ?? 99) - (order[b] ?? 99);
                })
                .map(([sev, n]) => `${sev}: ${n}`).join(' · ') || 'нажмите, чтобы открыть'}
            </div>
          </div>
          <span style={{
            fontSize: 12, color: filters.attention === 'mine' ? '#dc2626' : '#f59e0b',
            fontWeight: 600,
          }}>
            {filters.attention === 'mine' ? 'Сбросить' : 'Показать'}
          </span>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              Фильтр по критичности
            </label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="">Все уровни</option>
              <option value="critical">Критический</option>
              <option value="high">Высокий</option>
              <option value="medium">Средний</option>
              <option value="low">Низкий</option>
              <option value="info">Инфо</option>
            </select>
          </div>

          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              Фильтр по статусу
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="">Все статусы</option>
              <option value="open">Открыта</option>
              <option value="confirmed">Подтверждена</option>
              <option value="fixed">Исправлена</option>
              <option value="false_positive">Ложноположительная</option>
              <option value="accepted">Принята</option>
            </select>
          </div>

          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              Фильтр по хосту
            </label>
            <input
              type="text"
              placeholder="Фильтр по хосту..."
              value={filters.host}
              onChange={(e) => handleFilterChange('host', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          <div style={{ flex: '1', minWidth: '180px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              Исследователь
            </label>
            <select
              value={filters.researcher}
              onChange={(e) => handleFilterChange('researcher', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="">Все исследователи</option>
              {currentUsername && researchers.includes(currentUsername) && (
                <option value={currentUsername}>{currentUsername} (вы)</option>
              )}
              {researchers
                .filter((r) => r !== currentUsername)
                .map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              {hasUnassigned && <option value="__none__">Без исследователя</option>}
            </select>
          </div>

          <div style={{ alignSelf: 'flex-end' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              Просроченные
            </label>
            <button
              onClick={() => { handleFilterChange('overdue', !filters.overdue); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                border: `1px solid ${filters.overdue ? '#ef4444' : 'var(--border-color)'}`,
                borderRadius: '6px',
                backgroundColor: filters.overdue ? 'rgba(239,68,68,0.12)' : 'var(--bg-secondary)',
                color: filters.overdue ? '#ef4444' : 'var(--text-secondary)',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: filters.overdue ? '600' : '400',
              }}
            >
              <AlarmClock size={15} />
              {filters.overdue ? 'Сброс' : 'Показать'}
            </button>
          </div>

          <button
            onClick={fetchVulnerabilities}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#3b82f6',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              alignSelf: 'flex-end',
            }}
          >
            <RefreshCw size={16} />
            Обновить
          </button>
          <button
            onClick={() => { setShowImportModal(true); setImportSummary(null); setImportFileName(''); setImportPayload([]); }}
            className="btn btn-secondary"
            style={{ alignSelf: 'flex-end' }}
          >
            Импорт JSON
          </button>
          {isAdmin && (
            <button
              onClick={openPyrusSyncModal}
              className="btn btn-secondary"
              style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Settings size={15} />
              Pyrus Sync
            </button>
          )}
        </div>
      </div>

      {/* Vulnerabilities Table */}
      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <table className="data-table" style={{ 
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: 'var(--shadow)',
          border: '1px solid var(--border-color)'
        }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              <th
                onClick={() => handleSort('severity')}
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  borderBottom: '1px solid var(--border-color)',
                  width: '15%',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Критичность <SortIcon field="severity" />
                </span>
              </th>
              <th
                onClick={() => handleSort('title')}
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  borderBottom: '1px solid var(--border-color)',
                  width: '25%',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Название <SortIcon field="title" />
                </span>
              </th>
              <th
                onClick={() => handleSort('host')}
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  borderBottom: '1px solid var(--border-color)',
                  width: '20%',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Хост <SortIcon field="host" />
                </span>
              </th>
              <th
                onClick={() => handleSort('status')}
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  borderBottom: '1px solid var(--border-color)',
                  width: '15%',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Статус <SortIcon field="status" />
                </span>
              </th>
              <th
                onClick={() => handleSort('due_date')}
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  borderBottom: '1px solid var(--border-color)',
                  width: '12%',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Срок исправления <SortIcon field="due_date" />
                </span>
              </th>
              <th style={{
                padding: '16px 20px',
                textAlign: 'left',
                fontWeight: '600',
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '13%'
              }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ 
                  padding: '48px', 
                  textAlign: 'center', 
                  color: 'var(--text-secondary)',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <RefreshCw size={32} className="spin" />
                  <div style={{ marginTop: '16px' }}>Загрузка уязвимостей...</div>
                </td>
              </tr>
            ) : vulnerabilities.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ 
                  padding: '48px', 
                  textAlign: 'center', 
                  color: 'var(--text-secondary)',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <Shield size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <div>Уязвимости не найдены</div>
                </td>
              </tr>
            ) : (
              vulnerabilities.map((vuln) => {
                const mine = isMineUnclosed(vuln);
                const overdue = isOverdue(vuln);
                const expectedCloseDate = getExpectedCloseDate(vuln);
                const baseBg = mine ? 'rgba(220,38,38,0.07)' : (overdue ? 'rgba(239,68,68,0.04)' : 'transparent');
                const hoverBg = mine ? 'rgba(220,38,38,0.13)' : (overdue ? 'rgba(239,68,68,0.1)' : 'var(--bg-tertiary)');
                return (
                <tr key={vuln._id} style={{
                  borderBottom: '1px solid var(--border-color)',
                  transition: 'background-color 0.2s',
                  backgroundColor: baseBg,
                  borderLeft: mine ? '3px solid #dc2626' : undefined,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = baseBg; }}>
                  <td style={{ padding: '16px 20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: getSeverityColor(vuln.severity),
                      }}
                    >
                      {getSeverityIcon(vuln.severity)}
                      <span style={{ fontWeight: '600', textTransform: 'capitalize', fontSize: '14px' }}>
                        {vuln.severity}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '15px', color: 'var(--text-primary)' }}>{vuln.title}</div>
                      {vuln.cve_id && (
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {vuln.cve_id}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '14px', color: 'var(--text-primary)' }}>{vuln.host}</div>
                      {vuln.url !== vuln.host && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: 'var(--text-secondary)',
                            marginTop: '2px',
                          }}
                        >
                          {vuln.url}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <select
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        color: getStatusColor(vuln.status),
                        border: `1px solid ${getStatusColor(vuln.status)}`,
                        borderRadius: '4px',
                        backgroundColor: 'var(--bg-secondary)',
                        cursor: 'pointer'
                      }}
                      value={vuln.status}
                      onChange={(e) => handleUpdateStatus(vuln._id, e.target.value)}
                    >
                      <option value="open">Открыта</option>
                      <option value="confirmed">Подтверждена</option>
                      <option value="fixed">Исправлена</option>
                      <option value="false_positive">Ложноположительная</option>
                      <option value="accepted">Принята</option>
                    </select>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    {expectedCloseDate ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {overdue && <AlarmClock size={13} color="#ef4444" />}
                          <span style={{
                            fontSize: '13px',
                            fontWeight: overdue ? '600' : '400',
                            color: overdue ? '#ef4444' : 'var(--text-primary)',
                          }}>
                            {formatDate(expectedCloseDate)}
                          </span>
                        </div>
                        {hasIndividualDeadline(vuln) && (
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                            индивидуальный срок
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleViewDetails(vuln)}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #3b82f6',
                          borderRadius: '6px',
                          backgroundColor: 'transparent',
                          color: '#3b82f6',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#3b82f6';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#3b82f6';
                        }}
                        title="Показать детали"
                      >
                        <Eye size={14} />
                        Просмотр
                      </button>
                      <button
                        onClick={() => handleDelete(vuln._id)}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #ef4444',
                          borderRadius: '6px',
                          backgroundColor: 'transparent',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#ef4444';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#ef4444';
                        }}
                        title="Удалить"
                      >
                        <Trash2 size={14} />
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              );})
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            marginTop: '24px',
            padding: '16px',
            borderTop: '1px solid var(--border-color)',
          }}>
            <button
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                opacity: pagination.page === 1 ? 0.5 : 1,
              }}
            >
              Назад
            </button>
            <span style={{ color: 'var(--text-secondary)' }}>
              Страница {pagination.page} из {totalPages}
            </span>
            <button
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= totalPages}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                cursor: pagination.page >= totalPages ? 'not-allowed' : 'pointer',
                opacity: pagination.page >= totalPages ? 0.5 : 1,
              }}
            >
              Вперёд
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedVuln && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }} onClick={closeDetailModal}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Shield size={24} color="#3b82f6" />
                Детали уязвимости
              </h2>
              <button
                onClick={closeDetailModal}
                style={{
                  padding: '4px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Severity and Status */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      marginBottom: '4px',
                    }}
                  >
                    Критичность
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: getSeverityColor(selectedVuln.severity),
                      fontSize: '18px',
                      fontWeight: 'bold',
                    }}
                  >
                    {getSeverityIcon(selectedVuln.severity)}
                    <span style={{ textTransform: 'capitalize' }}>{selectedVuln.severity}</span>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      marginBottom: '4px',
                    }}
                  >
                    Статус
                  </div>
                  <div
                    style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: getStatusColor(selectedVuln.status),
                      textTransform: 'capitalize',
                    }}
                  >
                    {selectedVuln.status.replace('_', ' ')}
                  </div>
                </div>
              </div>

              {/* Title */}
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>{selectedVuln.title}</h3>
                {selectedVuln.description && (
                  <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                    {stripPyrusFromDescription(selectedVuln.description)}
                  </p>
                )}
              </div>

              {/* Dates */}
              {(selectedVuln.metadata?.discovery_date || getExpectedCloseDate(selectedVuln) ||
                selectedVuln.metadata?.fixed_date) && (
                <div style={{
                  display: 'flex',
                  gap: '24px',
                  marginBottom: '16px',
                  padding: '12px 16px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  flexWrap: 'wrap',
                }}>
                  {selectedVuln.metadata?.discovery_date && (
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Дата обнаружения</div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{formatDate(selectedVuln.metadata.discovery_date)}</div>
                    </div>
                  )}
                  {hasIndividualDeadline(selectedVuln) && selectedVuln.metadata?.due_date && (
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Крайний срок</div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{formatDate(selectedVuln.metadata.due_date)}</div>
                    </div>
                  )}
                  {hasIndividualDeadline(selectedVuln) && selectedVuln.metadata?.planned_fix_date && (
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Планируемая дата исправления</div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{formatDate(selectedVuln.metadata.planned_fix_date)}</div>
                    </div>
                  )}
                  {getExpectedCloseDate(selectedVuln) && (
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                        Ожидаемая дата закрытия
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: isOverdue(selectedVuln) ? '#ef4444' : 'var(--text-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}>
                        {isOverdue(selectedVuln) && <AlarmClock size={13} color="#ef4444" />}
                        {formatDate(getExpectedCloseDate(selectedVuln))}
                        {isOverdue(selectedVuln) && <span style={{ fontSize: '11px', fontWeight: '400' }}> — просрочено</span>}
                      </div>
                    </div>
                  )}
                  {selectedVuln.metadata?.fixed_date && (
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Дата исправления</div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#10b981' }}>{formatDate(selectedVuln.metadata.fixed_date)}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Responsible */}
              {(selectedVuln.researcher || selectedVuln.metadata?.researcher || selectedVuln.metadata?.responsible_person || selectedVuln.metadata?.responsible_department) && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '8px' }}>Ответственный</h4>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {(selectedVuln.researcher || selectedVuln.metadata?.researcher) && (
                      <div>
                        <strong>Исследователь:</strong>{' '}
                        <span style={{
                          color: (selectedVuln.researcher || selectedVuln.metadata?.researcher) === currentUsername
                            ? '#dc2626' : 'var(--text-primary)',
                          fontWeight: (selectedVuln.researcher || selectedVuln.metadata?.researcher) === currentUsername ? 600 : 400,
                        }}>
                          {selectedVuln.researcher || selectedVuln.metadata?.researcher}
                          {(selectedVuln.researcher || selectedVuln.metadata?.researcher) === currentUsername ? ' (вы)' : ''}
                        </span>
                      </div>
                    )}
                    {selectedVuln.metadata?.responsible_person && (
                      <div><strong>Сотрудник:</strong> {selectedVuln.metadata.responsible_person}</div>
                    )}
                    {selectedVuln.metadata?.responsible_department && (
                      <div><strong>Подразделение:</strong> {selectedVuln.metadata.responsible_department}</div>
                    )}
                  </div>
                </div>
              )}

              {/* CVE/CWE */}
              {(selectedVuln.cve_id || selectedVuln.cwe_id || selectedVuln.cvss_score) && (
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                  }}
                >
                  {selectedVuln.cve_id && (
                    <div>
                      <strong>CVE:</strong> {selectedVuln.cve_id}
                    </div>
                  )}
                  {selectedVuln.cwe_id && (
                    <div>
                      <strong>CWE:</strong> {selectedVuln.cwe_id}
                    </div>
                  )}
                  {selectedVuln.cvss_score && (
                    <div>
                      <strong>CVSS:</strong> {selectedVuln.cvss_score}
                    </div>
                  )}
                </div>
              )}

              {/* Target Information */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ marginBottom: '8px' }}>Цель</h4>
                <div style={{ fontSize: '14px' }}>
                  <div>
                    <strong>Хост:</strong> {selectedVuln.host}
                  </div>
                  <div>
                    <strong>URL:</strong>{' '}
                    <a
                      href={safeHref(selectedVuln.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#3b82f6' }}
                    >
                      {selectedVuln.url}
                    </a>
                  </div>
                  {selectedVuln.port && (
                    <div>
                      <strong>Порт:</strong> {selectedVuln.port}
                    </div>
                  )}
                  {selectedVuln.path && (
                    <div>
                      <strong>Путь:</strong> {selectedVuln.path}
                    </div>
                  )}
                </div>
              </div>

              {/* Evidence */}
              {selectedVuln.evidence &&
                (selectedVuln.evidence.request ||
                  selectedVuln.evidence.response ||
                  selectedVuln.evidence.proof) && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ marginBottom: '8px' }}>Доказательства</h4>
                    {selectedVuln.evidence.proof && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Подтверждение:</strong>
                        <pre
                          style={{
                            background: 'var(--bg-secondary)',
                            padding: '8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            overflow: 'auto',
                          }}
                        >
                          {selectedVuln.evidence.proof}
                        </pre>
                      </div>
                    )}
                    {selectedVuln.evidence.request && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Запрос:</strong>
                        <pre
                          style={{
                            background: 'var(--bg-secondary)',
                            padding: '8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            overflow: 'auto',
                            maxHeight: '200px',
                          }}
                        >
                          {selectedVuln.evidence.request}
                        </pre>
                      </div>
                    )}
                    {selectedVuln.evidence.response && (
                      <div>
                        <strong>Ответ:</strong>
                        <pre
                          style={{
                            background: 'var(--bg-secondary)',
                            padding: '8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            overflow: 'auto',
                            maxHeight: '200px',
                          }}
                        >
                          {selectedVuln.evidence.response}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

              {/* Business Impact */}
              {selectedVuln.metadata?.business_impact && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '8px' }}>Влияние на бизнес</h4>
                  <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{selectedVuln.metadata.business_impact}</p>
                </div>
              )}

              {/* Remediation */}
              {selectedVuln.remediation && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '8px' }}>Рекомендации по исправлению</h4>
                  <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{selectedVuln.remediation}</p>
                </div>
              )}

              {/* References */}
              {selectedVuln.references && selectedVuln.references.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '8px' }}>Ссылки</h4>
                  <ul style={{ paddingLeft: '20px' }}>
                    {selectedVuln.references.map((ref, idx) => (
                      <li key={idx}>
                        <a
                          href={safeHref(ref)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6' }}
                        >
                          {ref}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tags */}
              {selectedVuln.tags && selectedVuln.tags.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '8px' }}>Теги</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedVuln.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '4px 8px',
                          background: 'var(--bg-secondary)',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Source */}
              {selectedVuln.source && (
                <div>
                  <h4 style={{ marginBottom: '8px' }}>Источник</h4>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    <div>
                      <strong>Инструмент:</strong> {selectedVuln.source.tool}
                    </div>
                    {selectedVuln.source.template_id && (
                      <div>
                        <strong>Шаблон:</strong> {selectedVuln.source.template_id}
                      </div>
                    )}
                    {selectedVuln.source.task_id && (
                      <div>
                        <strong>ID задачи:</strong> {selectedVuln.source.task_id}
                      </div>
                    )}
                    {selectedVuln.seen_count > 1 && (
                      <div>
                        <strong>Обнаружений (дедуп):</strong> {selectedVuln.seen_count}
                        {selectedVuln.last_seen && ` · последнее ${new Date(selectedVuln.last_seen).toLocaleString()}`}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={closeDetailModal}
                style={{
                  padding: '10px 20px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '24px', width: '500px', maxWidth: '90vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>Импорт уязвимостей</h3>
              <button className="btn btn-secondary" onClick={() => setShowImportModal(false)}>Закрыть</button>
            </div>

            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Загрузите JSON-файл со списком уязвимостей. Минимальные поля: <code>title</code>, <code>host</code>. Дубликаты будут пропущены.
            </p>

            <input
              type="file"
              accept=".json,application/json"
              onChange={handleImportFile}
              style={{ marginBottom: '16px', display: 'block' }}
            />

            {importLoading && <div style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>Анализ файла...</div>}

            {importSummary && (
              <div style={{ backgroundColor: 'var(--bg-tertiary)', padding: '16px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
                <div><strong>Всего в файле:</strong> {importSummary.total_input}</div>
                <div style={{ color: 'var(--color-success-text)' }}><strong>Будет создано:</strong> {importSummary.created}</div>
                {importSummary.skipped_existing > 0 && <div><strong>Уже существуют:</strong> {importSummary.skipped_existing}</div>}
                {importSummary.skipped_duplicates_in_payload > 0 && <div><strong>Дубликаты в файле:</strong> {importSummary.skipped_duplicates_in_payload}</div>}
                {importSummary.invalid > 0 && <div style={{ color: 'var(--color-danger-text)' }}><strong>Некорректных:</strong> {importSummary.invalid}</div>}
                {importSummary.errors?.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-danger-text)' }}>
                    {importSummary.errors.map((err, i) => <div key={i}>Строка {err.index}: {err.message}</div>)}
                  </div>
                )}
              </div>
            )}

            {importSummary && importSummary.created > 0 && (
              <button className="btn btn-primary" onClick={applyImport} disabled={importLoading} style={{ width: '100%' }}>
                Импортировать {importSummary.created} уязвимостей
              </button>
            )}
          </div>
        </div>
      )}

      {/* Pyrus Sync Settings Modal (admin only) */}
      {showPyrusSyncModal && isAdmin && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '24px', width: '640px', maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Settings size={20} />
                Pyrus Sync
              </h3>
              <button className="btn btn-secondary" onClick={() => setShowPyrusSyncModal(false)}>Закрыть</button>
            </div>

            {pyrusConfigLoading && !pyrusConfig ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>Загрузка...</div>
            ) : (
              <>
                {/* Enable toggle */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={pyrusConfigForm.enabled || false}
                      onChange={(e) => setPyrusConfigForm((p) => ({ ...p, enabled: e.target.checked }))}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text-primary)' }}>
                      Автоматическая синхронизация включена
                    </span>
                  </label>
                </div>

                {/* Credentials */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '4px' }}>Base URL</label>
                    <input
                      type="text"
                      placeholder="https://pyrus.example.com"
                      value={pyrusConfigForm.base_url || ''}
                      onChange={(e) => setPyrusConfigForm((p) => ({ ...p, base_url: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '4px' }}>Registry ID</label>
                    <input
                      type="number"
                      placeholder="350860"
                      value={pyrusConfigForm.registry_id || ''}
                      onChange={(e) => setPyrusConfigForm((p) => ({ ...p, registry_id: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '4px' }}>Login</label>
                    <input
                      type="text"
                      placeholder="bot@..."
                      value={pyrusConfigForm.login || ''}
                      onChange={(e) => setPyrusConfigForm((p) => ({ ...p, login: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Security Key {pyrusConfig?.security_key_set && <span style={{ color: '#10b981', fontSize: '11px' }}>(задан)</span>}
                    </label>
                    <input
                      type="password"
                      placeholder={pyrusConfig?.security_key_set ? 'Оставьте пустым, чтобы не менять' : 'Security key'}
                      value={pyrusConfigForm.security_key || ''}
                      onChange={(e) => setPyrusConfigForm((p) => ({ ...p, security_key: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>

                {/* Schedule */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '4px' }}>Расписание</label>
                    <select
                      value={pyrusConfigForm.schedule_type || 'interval'}
                      onChange={(e) => setPyrusConfigForm((p) => ({ ...p, schedule_type: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    >
                      <option value="interval">Интервал</option>
                      <option value="cron">Cron</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      {pyrusConfigForm.schedule_type === 'cron' ? 'Cron выражение' : 'Интервал (напр. 24h, 12h, 30m)'}
                    </label>
                    <input
                      type="text"
                      placeholder={pyrusConfigForm.schedule_type === 'cron' ? '0 6 * * *' : '24h'}
                      value={pyrusConfigForm.schedule_expr || ''}
                      onChange={(e) => setPyrusConfigForm((p) => ({ ...p, schedule_expr: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '4px' }}>Задержка между запросами (мс)</label>
                    <input
                      type="number"
                      value={pyrusConfigForm.request_delay_ms ?? 100}
                      onChange={(e) => setPyrusConfigForm((p) => ({ ...p, request_delay_ms: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                  <button
                    onClick={savePyrusConfig}
                    disabled={pyrusConfigLoading}
                    className="btn btn-primary"
                    style={{ flex: '1' }}
                  >
                    {pyrusConfigLoading ? 'Сохранение...' : 'Сохранить настройки'}
                  </button>
                  <button
                    onClick={runPyrusSyncNow}
                    disabled={pyrusSyncRunning}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', border: 'none', borderRadius: '6px',
                      backgroundColor: '#10b981', color: 'white', cursor: 'pointer', fontSize: '14px',
                      opacity: pyrusSyncRunning ? 0.6 : 1,
                    }}
                  >
                    {pyrusSyncRunning ? <Loader size={15} className="spin" /> : <Play size={15} />}
                    Синхронизировать сейчас
                  </button>
                </div>

                {/* Info box */}
                <div style={{ backgroundColor: 'var(--bg-tertiary)', padding: '12px 16px', borderRadius: '8px', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: '1.6' }}>
                  Синхронизация забирает все уязвимости из реестра Pyrus и обновляет локальную базу.
                  Если вы вручную изменили статус уязвимости (например, поставили <strong>confirmed</strong> или <strong>false_positive</strong>),
                  он <strong>не будет перезаписан</strong> при следующей синхронизации.
                </div>

                {/* Sync runs history */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>История синхронизаций</h4>
                    <button onClick={fetchPyrusSyncRuns} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>
                      <RefreshCw size={12} />
                    </button>
                  </div>
                  {pyrusSyncRuns.length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center', padding: '16px' }}>
                      Синхронизаций ещё не было
                    </div>
                  ) : (
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                      {pyrusSyncRuns.map((run) => (
                        <div
                          key={run._id}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 12px', borderBottom: '1px solid var(--border-color)', fontSize: '13px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                              backgroundColor:
                                run.status === 'completed' ? '#10b981' :
                                run.status === 'failed' ? '#ef4444' :
                                run.status === 'running' ? '#3b82f6' : '#f59e0b',
                            }} />
                            <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                              {run.status === 'completed' ? 'Завершена' :
                               run.status === 'failed' ? 'Ошибка' :
                               run.status === 'running' ? 'Выполняется' : 'Ожидает'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            {run.stats && (
                              <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                                +{run.stats.created || 0} / ~{run.stats.updated || 0} / ={run.stats.unchanged || 0}
                                {run.stats.errors > 0 && <span style={{ color: '#ef4444' }}> / err:{run.stats.errors}</span>}
                              </span>
                            )}
                            {run.error && (
                              <span style={{ color: '#ef4444', fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={run.error}>
                                {run.error}
                              </span>
                            )}
                            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                              {run.created_at ? new Date(run.created_at).toLocaleString('ru-RU') : '—'}
                            </span>
                            <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                              {run.requested_by || ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Vulnerabilities;
